import { z } from 'zod';
import { listOf } from './common';
import { defineContract } from './contract';

const version = z.number().int().positive();
const permissionList = z.array(z.string().max(100)).max(500);

export const roleIdParams = z.strictObject({ id: z.uuid() });
export const membershipRoleParams = z.strictObject({ membershipId: z.uuid(), roleId: z.uuid() });
export const membershipParams = z.strictObject({ membershipId: z.uuid() });

export const createRoleBody = z.strictObject({
  name: z.string(),
  description: z.string().nullish(),
  permissions: permissionList.default([]),
});
export const updateRoleBody = z.strictObject({
  name: z.string().optional(),
  description: z.string().nullish(),
  version,
});
export const setPermissionsBody = z.strictObject({ permissions: permissionList, version });
export const deleteRoleQuery = z.strictObject({ version: z.coerce.number().int().positive() });
export const transferOwnershipBody = z.strictObject({ toMembershipId: z.uuid() });

export const roleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  system: z.string().nullable(),
  allPermissions: z.boolean(),
  permissions: z.array(z.string()),
  version: z.number().int(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type Role = z.infer<typeof roleSchema>;

export const myPermissionsSchema = z.object({
  membershipId: z.string(),
  owner: z.boolean(),
  permissions: z.array(z.string()),
});
export const platformPermissionsSchema = z.object({ permissions: z.array(z.string()) });

export const accessContracts = {
  myPermissions: defineContract({
    method: 'GET',
    path: '/authorization/me/permissions',
    response: myPermissionsSchema,
  }),
  myPlatformPermissions: defineContract({
    method: 'GET',
    path: '/authorization/me/platform-permissions',
    response: platformPermissionsSchema,
  }),
  listRoles: defineContract({
    method: 'GET',
    path: '/authorization/roles',
    response: listOf(roleSchema),
  }),
  createRole: defineContract({
    method: 'POST',
    path: '/authorization/roles',
    body: createRoleBody,
    response: roleSchema,
  }),
  getRole: defineContract({
    method: 'GET',
    path: '/authorization/roles/:id',
    response: roleSchema,
  }),
  updateRole: defineContract({
    method: 'PATCH',
    path: '/authorization/roles/:id',
    body: updateRoleBody,
    response: roleSchema,
  }),
  deleteRole: defineContract({
    method: 'DELETE',
    path: '/authorization/roles/:id',
    query: deleteRoleQuery,
    response: null,
  }),
  setRolePermissions: defineContract({
    method: 'PUT',
    path: '/authorization/roles/:id/permissions',
    body: setPermissionsBody,
    response: roleSchema,
  }),
  transferOwnership: defineContract({
    method: 'POST',
    path: '/authorization/ownership/transfer',
    body: transferOwnershipBody,
    response: null,
  }),
};
